/**
 * Workflow Service
 * 
 * Legacy workflow service.
 *
 * The old orchestration backend is deprecated. This module intentionally does
 * not make network calls, so lingering imports cannot hit /api/workflows.
 * 
 * @module services/workflow
 */

export const IS_AGENTIC_BACKEND = false;
export const WORKFLOW_BASE_URL = '';

// Workflow Types
export interface Workflow {
  id?: string | number;
  uuid?: string;
  name: string;
  user_id?: string;
  description?: string;
  status?: 'draft' | 'active' | 'inactive' | 'archived';
  inserted_at?: string;
  updated_at?: string;
  created_at?: string;
}

export interface PaginationInfo {
  page_size: number;
  page_number: number;
  total_entries: number;
  total_pages: number;
}

export interface WorkflowsListResponse {
  workflows: Workflow[];
  pagination: PaginationInfo;
}

export interface GetWorkflowsParams {
  user_id?: string;
  page?: number;
  page_size?: number;
}

/**
 * Fetch workflows with pagination
 */
export const getWorkflows = async (
  params: GetWorkflowsParams = {}
): Promise<WorkflowsListResponse> => {
  const page = params.page ?? 1;
  const pageSize = params.page_size ?? 10;
  return {
    workflows: [],
    pagination: {
      page_size: pageSize,
      page_number: page,
      total_entries: 0,
      total_pages: 1,
    },
  };
};

/**
 * Create a new workflow
 * POST /api/workflows
 */
export interface CreateWorkflowPayload {
  workflow: {
    name: string;
    user_id: string;
    org_id?: string;
    description?: string;
    status?: 'draft' | 'active' | 'inactive' | 'archived';
    metadata?: Record<string, any>;
  };
  nodes: Array<{
    temporary_id?: string;
    node_id?: string;
    name?: string;
    node_type: 'start' | 'end' | 'agent' | 'checkpoint' | 'rag' | 'guardrail' | 'if_else' | 'while_loop' | 'human_approval' | 'interrupt' | 'breakpoint';
    position: number;
    agent_id?: number;
    config?: Record<string, any>;
    agent?: {
      name: string;
      user_id: string;
      agent_type_id?: number;
      type?: string; // Legacy support
      description: string;
      prompt: string;
      llm_model_id: number;
      capabilities: number[] | Array<{ id: number; config?: Record<string, any> }>; // Support both formats
      config: {
        max_tokens: number;
        temperature: number;
      };
      metadata?: Record<string, any>;
    };
    metadata?: Record<string, any>;
  }>;
  edges: Array<{
    from_node_id: string;
    to_node_id: string;
    condition_type?: 'default' | 'if_true' | 'if_false';
    metadata?: Record<string, any>;
  }>;
}

export interface CreateWorkflowResponse {
  workflow: Workflow;
  nodes: any[];
  edges: any[];
}

export const createWorkflow = async (
  payload: CreateWorkflowPayload
): Promise<CreateWorkflowResponse> => {
  void payload;
  throw new Error('Workflow orchestration is deprecated. Use Agents, Migrations, or Scheduling.');
};

/**
 * Get workflow by ID with full graph (nodes and edges)
 * GET /api/workflows/:id
 */
export interface WorkflowDetailResponse {
  id: number;
  name: string;
  status: 'draft' | 'active' | 'inactive' | 'archived';
  description?: string;
  user_id: string;
  uuid: string;
  inserted_at: string;
  updated_at: string;
  start_node_id?: number;
  end_node_id?: number;
  metadata?: Record<string, any>;
  nodes: Array<{
    id: number;
    name: string;
    node_type: 'start' | 'end' | 'agent';
    position: number;
    uuid: string;
    agent_id?: number | null;
    agent?: {
      id: number;
      name: string;
      type: string;
      uuid: string;
    } | null;
    config?: Record<string, any>;
    metadata?: {
      position?: { x: number; y: number };
      measured?: { width: number; height: number };
      originalData?: Record<string, any>;
    };
    conditions?: Record<string, any>;
  }>;
  edges: Array<{
    id: number;
    from_node_id: number;
    to_node_id: number;
    uuid: string;
    condition_type?: string;
    condition_expression?: string | null;
    metadata?: Record<string, any>;
  }>;
}

export const getWorkflow = async (workflowId: string | number): Promise<WorkflowDetailResponse> => {
  void workflowId;
  throw new Error('Workflow orchestration is deprecated.');
};

/**
 * Update workflow graph
 * PUT /api/workflows/:id/graph
 */
export interface UpdateWorkflowPayload {
  workflow?: {
    name?: string;
    description?: string;
    status?: 'draft' | 'active' | 'inactive' | 'archived';
  };
  nodes: Array<{
    id?: number;              // For existing nodes
    temporary_id?: string;     // For new nodes
    name?: string;
    node_type: 'start' | 'end' | 'agent';
    position: number;
    agent_id?: number;         // For deployed agents
    agent?: {                  // For new agents
      name: string;
      user_id: string;
      agent_type_id?: number;
      type?: string;           // Legacy support
      description: string;
      prompt: string;
      llm_model_id: number;
      capabilities: number[];
      config: {
        max_tokens: number;
        temperature: number;
      };
      metadata?: Record<string, any>;
    };
  }>;
  edges: Array<{
    from_node_id: number | string;  // Can be node ID (number) or temporary_id (string)
    to_node_id: number | string;    // Can be node ID (number) or temporary_id (string)
  }>;
}

export interface UpdateWorkflowResponse {
  workflow: Workflow;
  nodes: any[];
  edges: any[];
}

export const updateWorkflow = async (
  workflowId: string | number,
  payload: UpdateWorkflowPayload
): Promise<UpdateWorkflowResponse> => {
  void workflowId;
  void payload;
  throw new Error('Workflow orchestration is deprecated. Use Scheduling for recurring runs.');
};

/**
 * Delete a workflow
 */
export const deleteWorkflow = async (workflowId: string | number): Promise<void> => {
  void workflowId;
  throw new Error('Workflow orchestration is deprecated.');
};

/**
 * execute a workflow
 */
export const executeWorkflow = async (workflowId: string | number): Promise<void> => {
  void workflowId;
  throw new Error('Workflow orchestration is deprecated.');
};
